import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTags, faPlus, faFolderPlus, faGear, faCheck, faTimes, faFolder } from '@fortawesome/free-solid-svg-icons';
import { tagsApi } from '../../store/tags/tagsApi';
import { GhostSelect } from '../ui/GhostSelect';
import BtnTaratic from '../ui/BtnTaratic';
import { SlidingPills } from '../ui/SlidingPills';
import { TagBadge } from '../ui/TagBadge';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import { mergeThreadTags } from '../../store/chat/chatSlice';
import { useNotifications } from '../ui/notifications';

interface ThreadTagsSelectorProps {
  threadId: string;
  assignedTags: any[];
}

export const ThreadTagsSelector: React.FC<ThreadTagsSelectorProps> = ({ threadId, assignedTags }) => {
  const navigate = useNavigate();
  const { data, isLoading } = tagsApi.useGetTagsWithFoldersQuery();
  const [createTag] = tagsApi.useCreateTagMutation();
  const [createFolder] = tagsApi.useCreateFolderMutation();
  const [addTagToThread] = tagsApi.useAddTagToThreadMutation();
  const [removeTagFromThread] = tagsApi.useRemoveTagFromThreadMutation();
  const dispatch = useAppDispatch();
  const theme = useAppSelector((state) => state.ui.theme);
  const notifications = useNotifications();

  // Sync with Redux list (sidebar) whenever the header fetches latest tags
  useEffect(() => {
    if (assignedTags && threadId) {
      dispatch(mergeThreadTags({ [String(threadId)]: assignedTags }));
    }
  }, [assignedTags, threadId, dispatch]);

  const [isUpdating, setIsUpdating] = useState(false);

  const [showConfigPopover, setShowConfigPopover] = useState(false);
  const [activeTab, setActiveTab] = useState<'assign' | 'tag' | 'folder'>('assign');

  const [newTagName, setNewTagName] = useState('');
  const [newTagColor, setNewTagColor] = useState('#3b82f6');
  const [newTagFolderId, setNewTagFolderId] = useState<number | null>(null);

  const [newFolderName, setNewFolderName] = useState('');
  const [newFolderParentId, setNewFolderParentId] = useState<number | null>(null);

  const configBtnRef = useRef<HTMLButtonElement>(null);
  const popoverRef = useRef<HTMLDivElement>(null);

  // Close popover on click outside
  useEffect(() => {
    if (!showConfigPopover) return;
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      if (popoverRef.current && !popoverRef.current.contains(target) &&
        configBtnRef.current && !configBtnRef.current.contains(target) &&
        !target.closest('[data-ghost-dropdown="true"]')) {
        setShowConfigPopover(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showConfigPopover]);

  // Position calculation for portal
  const [popoverStyle, setPopoverStyle] = useState<React.CSSProperties>({});
  useEffect(() => {
    if (showConfigPopover && configBtnRef.current) {
      const rect = configBtnRef.current.getBoundingClientRect();
      setPopoverStyle({
        top: rect.bottom + 8,
        left: rect.left,
      });
    }
  }, [showConfigPopover]);

  const globalTags = data?.tags || [];
  const globalFolders = data?.folders || [];
  const assignedTagIds = assignedTags.map(tag => String(tag.id));

  const PREDEFINED_COLORS = ['#ef4444', '#f97316', '#eab308', '#22c55e', '#3b82f6', '#8b5cf6', '#ec4899', '#64748b'];

  const getFolderPath = (folderId: number): string => {
    let currentFolder = globalFolders.find((f: any) => f.id === folderId);
    if (!currentFolder) return '';
    const path = [currentFolder.name];
    let parentId = currentFolder.parent_id;
    while (parentId) {
      const parent = globalFolders.find((f: any) => f.id === parentId);
      if (parent) {
        path.unshift(parent.name);
        parentId = parent.parent_id;
      } else {
        break;
      }
    }
    return path.join(' / ');
  };

  const options = globalTags.map(tag => {
    const folderPath = tag.folder_id ? getFolderPath(tag.folder_id) : null;
    return {
      value: String(tag.id),
      label: <TagBadge tag={tag} size="sm" />,
      textLabel: tag.name,
      subtitle: folderPath ? (
        <span className="flex items-center gap-1.5 mt-0.5">
          <FontAwesomeIcon icon={faFolder} className="text-[10px] text-gray-500" />
          <span className="truncate max-w-[200px]">{folderPath}</span>
        </span>
      ) : undefined,
      folder_id: tag.folder_id ? String(tag.folder_id) : undefined,
    };
  });

  const folderOptions = globalFolders.map((f: any) => {
    const folderTags = globalTags.filter((t: any) => t.folder_id === f.id);
    const previewTags = folderTags.slice(0, 3).map((t: any) => t.name).join(', ');
    const suffix = previewTags ? `Tags: ${previewTags}${folderTags.length > 3 ? '...' : ''}` : '';

    return {
      value: String(f.id),
      label: getFolderPath(f.id),
      subtitle: suffix ? (
        <span className="flex items-center gap-1.5 mt-0.5">
          <FontAwesomeIcon icon={faTags} className="text-[10px] text-gray-500" />
          <span className="truncate max-w-[200px] text-gray-400">{suffix}</span>
        </span>
      ) : undefined,
      icon: <FontAwesomeIcon icon={faFolder} className="text-gray-400" />
    };
  });

  const handleChange = async (newSelectedIds: string[]) => {
    setIsUpdating(true);
    try {
      const added = newSelectedIds.filter(id => !assignedTagIds.includes(id));
      const removed = assignedTagIds.filter(id => !newSelectedIds.includes(id));

      for (const id of added) {
        await addTagToThread({ threadId, tag_id: Number(id) }).unwrap();
      }
      for (const id of removed) {
        await removeTagFromThread({ threadId, tagId: Number(id) }).unwrap();
      }
    } catch (err) {
      console.error('Failed to update tags', err);
      alert('Error al actualizar tags');
    } finally {
      setIsUpdating(false);
    }
  };

  const handleCreateTag = async () => {
    const name = newTagName.trim();
    if (!name) {
      setShowConfigPopover(false);
      notifications.warning('Revisa los datos', 'El nombre del tag es obligatorio.');
      return;
    }
    try {
      setIsUpdating(true);
      await createTag({
        name,
        color: newTagColor,
        folder_id: newTagFolderId
      }).unwrap();
      setNewTagName('');
      setNewTagColor('#3b82f6');
      setNewTagFolderId(null);
      setShowConfigPopover(false);
      notifications.success('Tag creado', `${name} se añadió correctamente.`);
    } catch {
      notifications.error('No se pudo crear el tag', 'Inténtalo de nuevo.');
    } finally {
      setIsUpdating(false);
    }
  };

  const handleCreateFolder = async () => {
    const name = newFolderName.trim();
    if (!name) {
      setShowConfigPopover(false);
      notifications.warning('Revisa los datos', 'El nombre de la carpeta es obligatorio.');
      return;
    }
    try {
      setIsUpdating(true);
      await createFolder({
        name,
        parent_id: newFolderParentId
      }).unwrap();
      setNewFolderName('');
      setNewFolderParentId(null);
      setShowConfigPopover(false);
      notifications.success('Carpeta creada', `${name} se añadió correctamente.`);
    } catch {
      notifications.error('No se pudo crear la carpeta', 'Inténtalo de nuevo.');
    } finally {
      setIsUpdating(false);
    }
  };

  if (isLoading) {
    return <div data-ui="thread-tags-loading" className="animate-pulse w-24 h-6 bg-white/10 rounded-full"></div>;
  }

  return (
    <>
      <div data-ui="thread-tags-selector" data-theme={theme} className="relative flex items-center gap-2">
        {/* Settings/Tags Button */}
        <button
          data-ui="thread-tags-trigger"
          ref={configBtnRef}
          onClick={() => setShowConfigPopover(!showConfigPopover)}
          disabled={isUpdating}
          className={`cursor-pointer flex items-center gap-2 px-3 py-1.5 rounded-full border transition-colors ${showConfigPopover ? 'bg-blue-600/80 border-blue-500 text-white shadow-[0_0_10px_rgba(37,99,235,0.5)]' : 'bg-white/5 hover:bg-white/10 border-white/10 text-gray-300 hover:text-white'}`}
          title="Etiquetas y Configuración"
        >
          <div data-ui="thread-tags-region-001" className="flex items-center gap-0.5">
            <FontAwesomeIcon icon={faPlus} className="text-[10px]" />
            <FontAwesomeIcon icon={faTags} className="text-xs" />
          </div>
          <span className="text-xs font-medium whitespace-nowrap">Tags & Carpetas</span>
        </button>

        {/* Portaled Popover */}
        {showConfigPopover && typeof document !== 'undefined' && createPortal(
          <div
            data-ui="thread-tags-popover"
            data-theme={theme}
            ref={popoverRef}
            style={popoverStyle}
            className="thread-tags-theme fixed z-[99999] w-[340px] bg-black/60 backdrop-filter backdrop-blur-xl border border-white/20 rounded-2xl shadow-[0_8px_32px_rgba(0,0,0,0.8)] overflow-hidden"
          >
            <div data-ui="thread-tags-popover-header" className="p-3 border-b border-white/10 flex items-center justify-between">
              <div data-ui="thread-tags-region-002" className="flex items-center gap-3">
                <h3 className="text-xs font-semibold text-white/80 uppercase tracking-wider">Configuración</h3>
                <button
                  onClick={() => {
                    setShowConfigPopover(false);
                    navigate('/tags-config');
                  }}
                  className="btn-taratic btn-ghost-dark btn-xs flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium"
                  title="Configuración Avanzada"
                >
                  <FontAwesomeIcon icon={faGear} />
                  <span>
                    Avanzada
                  </span>
                </button>
              </div>
              <button onClick={() => setShowConfigPopover(false)} className="!bg-transparent !border-none !shadow-none text-gray-400 hover:text-white transition-colors p-1 flex items-center justify-center">
                <FontAwesomeIcon icon={faTimes} className="text-xs" />
              </button>
            </div>
            <div data-ui="thread-tags-popover-content" className="p-3">
              {/* Tabs */}
              <div data-ui="thread-tags-region-003" className="mb-4 flex justify-center w-full">
                <SlidingPills
                  options={[
                    { id: 'assign', label: 'Asignar', icon: faTags },
                    { id: 'tag', label: 'Nuevo Tag', icon: faPlus },
                    { id: 'folder', label: 'Carpeta', icon: faFolderPlus }
                  ]}
                  activeId={activeTab}
                  onChange={(id) => setActiveTab(id as 'assign' | 'tag' | 'folder')}
                  size="xs"
                  variant={theme}
                  className="w-full"
                />
              </div>

              {/* Form */}
              {activeTab === 'assign' && (
                <div data-ui="thread-tags-region-004" className="space-y-3 relative z-50">
                  <GhostSelect
                    options={options}
                    value={assignedTagIds}
                    onChange={handleChange}
                    multiple={true}
                    searchable={true}
                    searchPlaceholder="Buscar tags..."
                    placeholder="Buscar o seleccionar tags..."
                    disabled={isUpdating}
                    className="w-full text-xs"
                  />
                </div>
              )}
              {activeTab === 'tag' && (
                <div data-ui="thread-tags-region-005" className="space-y-3">
                  <input
                    type="text"
                    value={newTagName}
                    onChange={(e) => setNewTagName(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && handleCreateTag()}
                    autoFocus
                    placeholder="Nombre del nuevo tag..."
                    className="w-full bg-black/75 border border-white/10 text-white text-xs px-3 py-2 rounded-xl focus:outline-none focus:border-white/30 transition-colors"
                  />

                  {globalFolders.length > 0 && (
                    <div data-ui="thread-tags-region-006" className="relative w-full z-50">
                      <GhostSelect
                        options={folderOptions}
                        value={newTagFolderId ? String(newTagFolderId) : ''}
                        onChange={(val) => setNewTagFolderId(val ? Number(val) : null)}
                        multiple={false}
                        placeholder="Sin carpeta (Opcional)"
                        disabled={isUpdating}
                        className="w-full text-xs"
                      />
                    </div>
                  )}

                  <div data-ui="thread-tags-region-007" className="flex flex-col gap-2">
                    <div data-ui="thread-tags-region-008" className="flex gap-1.5 justify-center py-1 flex-wrap">
                      {PREDEFINED_COLORS.map(c => (
                        <button
                          key={c}
                          onClick={() => setNewTagColor(c)}
                          className={`w-5 h-5 rounded-full !border-2 !p-0 ${newTagColor === c ? '!border-white scale-110 shadow-[0_0_8px_rgba(255,255,255,0.5)]' : '!border-transparent opacity-70 hover:opacity-100'}`}
                          style={{ backgroundColor: c }}
                          title={c}
                          type="button"
                        />
                      ))}
                    </div>
                    <div data-ui="thread-tags-region-009" className="flex items-center gap-2">
                      <label
                        className="w-6 h-6 rounded-full border border-white/20 shrink-0 overflow-hidden cursor-pointer relative flex items-center justify-center hover:scale-105 transition-transform"
                      >
                        <div data-ui="thread-tags-region-010" className="absolute inset-0 pointer-events-none" style={{ backgroundColor: newTagColor }} />
                        <input
                          type="color"
                          value={newTagColor}
                          onChange={(e) => setNewTagColor(e.target.value)}
                          className="opacity-0 w-10 h-10 cursor-pointer absolute"
                        />
                      </label>
                      <input
                        type="text"
                        value={newTagColor}
                        onChange={(e) => setNewTagColor(e.target.value)}
                        placeholder="#000000"
                        className="flex-1 bg-black/75 border border-white/10 text-white text-xs px-2 py-1.5 rounded-lg focus:outline-none focus:border-white/30 transition-colors uppercase"
                      />
                    </div>
                  </div>

                  <BtnTaratic color="blue"
                    onClick={handleCreateTag}
                    disabled={isUpdating || !newTagName.trim()}
                    className="w-full flex items-center justify-center gap-2"
                  >
                    <FontAwesomeIcon icon={faCheck} /> Crear Tag
                  </BtnTaratic>
                </div>
              )}
              {activeTab === 'folder' && (
                <div data-ui="thread-tags-region-011" className="space-y-3 relative">
                  <input
                    type="text"
                    value={newFolderName}
                    onChange={(e) => setNewFolderName(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && handleCreateFolder()}
                    autoFocus
                    placeholder="Nombre de la carpeta..."
                    className="w-full bg-black/75 border border-white/10 text-white text-xs px-3 py-2 rounded-xl focus:outline-none focus:border-white/30 transition-colors"
                  />

                  {globalFolders.length > 0 && (
                    <div data-ui="thread-tags-region-012" className="relative w-full z-40">
                      <GhostSelect
                        options={folderOptions}
                        value={newFolderParentId ? String(newFolderParentId) : ''}
                        onChange={(val) => setNewFolderParentId(val ? Number(val) : null)}
                        multiple={false}
                        placeholder="Subcarpeta de... (Opcional)"
                        disabled={isUpdating}
                        className="w-full text-xs"
                      />
                    </div>
                  )}

                  <BtnTaratic color="blue"
                    onClick={handleCreateFolder}
                    disabled={isUpdating || !newFolderName.trim()}
                    className="w-full flex items-center justify-center gap-2"
                  >
                    <FontAwesomeIcon icon={faCheck} /> Crear Carpeta
                  </BtnTaratic>
                </div>
              )}
            </div>
          </div>,
          document.body
        )}
      </div>
    </>
  );
};
